import { useEffect, useRef, useState } from "react";

import { fetchDocumentSource } from "@/api/documents";
import { useAccessToken } from "@/auth/useAccessToken";
import { PageImageWithBbox } from "@/components/citations/BboxOverlay";
import { citationLocation } from "@/lib/citationLocation";
import { isTopModal, useModalStack } from "@/lib/modalStack";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { useChatStore } from "@/runtime/chatStore";
import { buildSourceNumberByChunkId } from "@/runtime/citationDiscriminator";
import type { PersistedCitationRef } from "@/types";

const MODAL_ID = "citation-panel";

// Component-local per the codebase convention InlineFootnotes.tsx also follows
// (its own short-form helper). Originally inlined from SourcesList.tsx — a copy,
// never an import, which is why deleting that file on 2026-08-09 left this panel
// untouched. Drift risk is bounded by the one-line shape.
function shortenDocId(docId: string): string {
  const lastDash = docId.lastIndexOf("-");
  if (lastDash > 0 && docId.length > 40) {
    return docId.slice(0, 16) + "…" + docId.slice(lastDash);
  }
  if (docId.length > 40) return docId.slice(0, 37) + "…";
  return docId;
}

// Shown when a citation has no page screenshot, AND when its screenshot URL
// fails to load (PageImageWithBbox `fallback`): both are the same "no preview" state.
function NoPagePreview() {
  return (
    <div
      role="note"
      aria-label="Page preview not available"
      className="flex min-h-24 flex-col items-center justify-center gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-dashed border-[var(--border-default)] bg-[var(--surface-sunken)] px-[var(--space-4)] py-[var(--space-6)] text-center italic text-[var(--bk-fg-muted)]"
    >
      <svg
        width="28"
        height="28"
        viewBox="0 0 18 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="opacity-70"
      >
        <rect x="2.75" y="3.5" width="12.5" height="11" rx="1.75" />
        <circle cx="6.5" cy="7.25" r="1.1" />
        <path d="M3.25 12.5l3.25-2.75 2.5 2 2.25-1.75 3.25 3" />
      </svg>
      <span className="type-body-sm not-italic">Page preview not available for this source</span>
    </div>
  );
}

function findCitation(
  messages: ReturnType<typeof useChatStore.getState>["messages"],
  chunkId: string | null,
): { cite: PersistedCitationRef; displayIdx: number } | null {
  if (chunkId === null) return null;
  // Lookup by chunk_id (the stable identifier across same-chunk sibling
  // PersistedCitationRefs) with two-pass tie-break: (1) prefer the cite carrying
  // page_image_url so the panel renders the thumbnail when ANY same-chunk
  // sibling has one (Bug Class 1 fix from the 10th-iter structural review
  // — pre-fix the index-key lookup resolved to a DIFFERENT sibling than
  // the card had displayed, breaking image continuity between card+panel);
  // (2) compute displayIdx = LOWEST index across all same-chunk siblings
  // so the panel header label matches the chip+card canonical labels.
  // Search messages in order, return first message with any matching cite.
  for (const m of messages) {
    let best: PersistedCitationRef | null = null;
    let lowestIdx = Number.POSITIVE_INFINITY;
    for (const c of m.citations ?? []) {
      if (c.chunk_id !== chunkId) continue;
      if (c.index < lowestIdx) lowestIdx = c.index;
      if (best === null) {
        best = c;
        continue;
      }
      const bestHasImg = typeof best.page_image_url === "string" && best.page_image_url.length > 0;
      const candidateHasImg = typeof c.page_image_url === "string" && c.page_image_url.length > 0;
      if (!bestHasImg && candidateHasImg) best = c;
    }
    if (best !== null) {
      const lowestFallback = lowestIdx === Number.POSITIVE_INFINITY ? best.index : lowestIdx;
      // v6.5: attribution cohort → stable per-source number (matches pill + strip); else canonical lowest.
      const displayIdx = buildSourceNumberByChunkId(m.citations ?? []).get(chunkId) ?? lowestFallback;
      return { cite: best, displayIdx };
    }
  }
  return null;
}

// Why the source_uri is fetched HERE instead of arriving on CitationEvent:
// `_rebind_citations` runs on rag-query (ADR-091 worker path) which is
// bake-frozen at rag-query--0000038 through 2026-06-07 (ADR-092 Phase 2).
// Adding source_uri to the stream-event payload would require a rag-query
// redeploy and reset the bake clock. The dedicated chat-user endpoint
// `GET /v1/tenants/{tid}/documents/{doc_id}/source` lives on rag-api so
// the citation panel can fetch source_uri on-demand without touching the
// query path.
type SourceState =
  | { kind: "idle" }
  | { kind: "loading"; docId: string }
  | { kind: "ready"; docId: string; source_uri: string; original_file_name: string }
  | { kind: "error"; docId: string };

export function CitationDetailPanel() {
  const activeCitationChunkId = useChatStore((s) => s.activeCitationChunkId);
  const setActiveCitationChunkId = useChatStore((s) => s.setActiveCitationChunkId);
  const messages = useChatStore((s) => s.messages);
  const activeTenantId = useChatStore((s) => s.activeTenantId);
  const found = findCitation(messages, activeCitationChunkId);
  const cite = found?.cite ?? null;
  const displayIdx = found?.displayIdx ?? null;
  const dialogRef = useRef<HTMLElement | null>(null);
  const isOpen = cite !== null;
  const { acquireToken } = useAccessToken();

  // source_uri state — separate from cite because cite comes from
  // CitationEvent (no source_uri field) and source_uri comes from
  // the dedicated endpoint. Resets on every distinct doc_id so rapid
  // chip clicks don't display stale state.
  const [sourceState, setSourceState] = useState<SourceState>({ kind: "idle" });

  useModalStack(MODAL_ID, isOpen);
  useFocusTrap(dialogRef, isOpen);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && isTopModal(MODAL_ID)) setActiveCitationChunkId(null);
    }
    if (isOpen) {
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    return undefined;
  }, [isOpen, setActiveCitationChunkId]);

  // Fetch source_uri whenever the active doc_id changes. AbortController
  // cancels in-flight fetches on rapid chip switching so the displayed
  // "Open source" button always reflects the currently-open citation.
  // Reset to {kind: idle} when the panel closes so a future re-open
  // re-fetches (covers the case where backend data changed mid-session).
  //
  // Critical: dep on cite?.doc_id (a stable primitive) NOT cite (which
  // is a fresh reference per render from .find() in findCitation).
  // Depending on the object reference would create an infinite
  // re-render loop: render → cite is new → effect runs → setState
  // → render → loop.
  const activeDocId = cite?.doc_id ?? null;
  useEffect(() => {
    if (!activeDocId || !activeTenantId) {
      setSourceState({ kind: "idle" });
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;

    setSourceState({ kind: "loading", docId: activeDocId });

    (async () => {
      try {
        const token = await acquireToken();
        const resp = await fetchDocumentSource(activeTenantId, activeDocId, token, {
          signal: controller.signal,
        });
        if (cancelled) return;
        setSourceState({
          kind: "ready",
          docId: activeDocId,
          source_uri: resp.source_uri,
          original_file_name: resp.original_file_name,
        });
      } catch (err) {
        if (cancelled || (err as { name?: string })?.name === "AbortError") return;
        setSourceState({ kind: "error", docId: activeDocId });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activeDocId, activeTenantId, acquireToken]);

  if (!cite) return null;

  const pageNumber = typeof cite.page_idx === "number" && cite.page_idx >= 0 ? cite.page_idx + 1 : null;
  const location = citationLocation(cite);
  const hasPageImage = typeof cite.page_image_url === "string" && cite.page_image_url.length > 0;
  const displayTitle =
    typeof cite.doc_title === "string" && cite.doc_title.length > 0 ? cite.doc_title : shortenDocId(cite.doc_id);
  const hasJaccard = typeof cite.rebind_jaccard === "number" && cite.rebind_jaccard >= 0;

  // source_uri visibility: only render the Open-source link when the
  // fetch resolved AND the URL is non-empty. Empty source_uri ("") means
  // either (a) PG NULL was normalized at the wire, or (b) the document
  // genuinely has no source-system URL (e.g. file_upload connector with
  // no webUrl) — gracefully no button rendered. The loading-state
  // placeholder helps operators understand the panel is hydrating.
  const sourceUri = sourceState.kind === "ready" && sourceState.docId === cite.doc_id ? sourceState.source_uri : "";
  const isLoadingSource = sourceState.kind === "loading" && sourceState.docId === cite.doc_id;
  const showOpenSourceLink = sourceUri.length > 0;

  return (
    <>
      {/* Backdrop is mobile-only (< md). On desktop ≥ md the panel becomes
          an in-flow flex sibling of <main> (canvas split-pane per operator
          2026-06-02), so no backdrop dimming is needed — the panel pushes
          the main column left via flex shrink instead of overlaying. */}
      <div
        className="bk-anim-backdrop fixed inset-0 z-40 bg-[var(--scrim)] md:hidden"
        onClick={() => setActiveCitationChunkId(null)}
        aria-hidden="true"
      />
      {/* role="complementary" is IMPLICIT on <aside> per HTML5 spec —
          no explicit role attribute needed (linter rejects redundancy).
          testing-library's getByRole("complementary") still resolves via
          the implicit semantic. Operator-explicit canvas pane (2026-06-02)
          replaces the prior role="dialog" which is wrong now that this
          is in-flow content (not a modal overlay) on desktop. */}
      <aside
        ref={dialogRef}
        aria-label="Citation detail"
        // Mobile (< md): fixed overlay (right-0, full width, z-50). Keeps
        // the prior dialog-style behavior for narrow viewports where there
        // is no horizontal room to split.
        // Desktop (≥ md): in-flow flex sibling of <main> at md:w-[60vw].
        // `md:relative md:z-auto` removes overlay positioning; flex parent
        // (AppShell root row, line 168) handles layout. <main> has min-w-0
        // so it shrinks to accommodate this canvas pane. transition-all
        // animates the panel width on open/close (no backdrop fade needed).
        // Operator-explicit (2026-06-02): "chat display in the middle will
        // automatically go left when the right pane opens".
        className="fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-[var(--border-default)] bg-[var(--surface-raised)] shadow-[var(--elev-whisper-xl)] transition-all duration-200 md:relative md:z-auto md:w-[60vw] md:flex-shrink-0"
      >
        <header className="flex items-center justify-between gap-[var(--space-3)] border-b border-[var(--border-default)] px-[var(--space-4)] py-[var(--space-3)]">
          <div className="flex items-center gap-[var(--space-2)]">
            <span className="type-title text-[var(--text-primary)]">Source {displayIdx ?? cite.index}</span>
            {location !== null && (
              <span className="rounded-[var(--radius-sm)] bg-[var(--citation-bg)] px-[var(--space-2)] py-0.5 type-caption font-semibold text-[var(--citation-fg)]">
                {location.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showOpenSourceLink && (
              <a
                href={sourceUri}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open original document in new tab"
                title={sourceUri}
                className="inline-flex items-center gap-[var(--space-1)] rounded-[var(--radius-pill)] border border-[var(--accent-primary)] bg-transparent px-[var(--space-3)] py-[var(--space-1)] type-caption font-medium text-[var(--accent-text)] no-underline transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--accent-primary)] hover:text-[var(--text-on-accent)] focus:outline-none focus-visible:shadow-[var(--elev-focus)]"
              >
                Open source
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4.5 2.5H9.5V7.5" />
                  <path d="M9.5 2.5L3 9" />
                </svg>
              </a>
            )}
            {isLoadingSource && (
              <span className="type-caption italic text-[var(--text-tertiary)]" aria-live="polite">
                Loading…
              </span>
            )}
            <button
              type="button"
              onClick={() => setActiveCitationChunkId(null)}
              aria-label="Close citation panel"
              className="inline-flex items-center justify-center rounded-[var(--radius-pill)] p-[var(--space-1)] text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--elev-focus)]"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>
        </header>

        <div className="flex-1 space-y-[var(--space-4)] overflow-y-auto px-[var(--space-4)] py-[var(--space-4)]">
          <div>
            <h2 className="type-title truncate text-[var(--text-primary)]">{displayTitle}</h2>
            <p className="mt-[var(--space-1)] truncate type-caption font-mono text-[var(--text-tertiary)]">
              {cite.doc_id}
            </p>
          </div>
          {hasPageImage ? (
            // Operator-explicit (2026-06-02): "screenshot displayed on
            // the right pane is very small, which is not readable by
            // the users at normal eye sight". With the canvas pane at
            // md:w-[60vw] wide and panel body using overflow-y-auto (line 212),
            // dropping max-h-[55vh] + min-h-64 lets the thumbnail's
            // natural aspect ratio drive its rendered size at full
            // canvas width — typically much larger than the prior
            // cramped 55vh cap.
            <PageImageWithBbox
              src={cite.page_image_url!}
              alt={`Page ${pageNumber ?? "?"} of ${displayTitle}`}
              bbox={cite.bbox}
              bboxList={cite.bbox_list}
              className="block w-full rounded-[var(--radius-lg)] bg-[var(--surface-sunken)] object-contain shadow-[var(--elev-ring)]"
              fallback={<NoPagePreview />}
            />
          ) : (
            // Defect #2 (2026-06-02 click-only e2e audit): operator opened
            // the panel for a proposition-chunk citation (ADR-066 Wave C —
            // text-heavy industrial PDFs where the chunk spans multiple
            // page blocks so the backend skips per-chunk page-screenshot
            // capture) and saw a blank space where the image goes. The
            // silent omission was indistinguishable from a render bug.
            // This placeholder confirms the missing-image state is
            // intentional — `page_image_url=null` means "no screenshot
            // available", NOT "frontend failed to load it". When the
            // chunk DOES have a screenshot, PageImageWithBbox renders
            // the `<img>` (verified by BboxOverlay tests).
            <NoPagePreview />
          )}
          <p className="whitespace-pre-wrap type-body text-[var(--text-primary)]">{cite.preview}</p>
          {hasJaccard && (
            <span
              className="inline-flex items-center gap-[var(--space-1)] rounded-full bg-[var(--accent-soft)] px-[var(--space-2)] py-0.5 type-caption text-[var(--accent-text)]"
              title="Citation-chunk overlap (T8 cite_align Jaccard)"
            >
              J={cite.rebind_jaccard?.toFixed(2)}
            </span>
          )}
          {cite.supporting_chunk_ids && cite.supporting_chunk_ids.length > 0 && (
            <div
              data-testid="multi-source-footer"
              className="mt-[var(--space-3)] border-t border-[var(--border-default)] pt-[var(--space-2)] type-caption text-[var(--text-tertiary)]"
            >
              <div className="mb-[var(--space-1)] type-overline text-[var(--text-tertiary)]">
                Other supporting sources ({cite.supporting_chunk_ids.length})
              </div>
              <ul className="space-y-0.5">
                {cite.supporting_chunk_ids.map((sid) => (
                  <li key={`supporting-${sid}`}>
                    <button
                      type="button"
                      data-testid={`supporting-chunk-${sid}`}
                      onClick={() => setActiveCitationChunkId(sid)}
                      className="font-mono text-[var(--accent-text)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:underline focus:outline-none focus-visible:shadow-[var(--elev-focus)]"
                    >
                      {shortenDocId(sid)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
